<script setup lang="ts">
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import {
  StayTag,
  HotelStayTagType,
} from '@apps/main/new-desktop/modules/stayTag';
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';

import { AppLinkButton, AppStars, AppTag } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import type {
  AccommodationProductCardContent,
  AccommodationSegmentContext,
} from '../../../models';

import AccommodationAmenities from './AccommodationAmenities.vue';

interface AccommodationProductCardContentProps {
  productCardContent: AccommodationProductCardContent;
  context: AccommodationSegmentContext;
  printMode: boolean;
}

const props = defineProps<AccommodationProductCardContentProps>();
interface AccommodationProductCardContentEmits {
  (eventName: 'mouse-enter-content'): void;
  (eventName: 'mouse-leave-content'): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-rooms'): void;
}

const emit = defineEmits<AccommodationProductCardContentEmits>();

const browserStore = useBrowserStore();

const hasMultipleRooms = computed<boolean>(
  () => props.productCardContent.cardDetails.length > 1
);

const isHotelGuestRatingExperiment = computedAsync(
  () => isExperimentAsync('hotel_guest_rating'),
  false
);

function onEditRoomsBannerClick(): void {
  if (browserStore.isSmallerThan1200px) {
    emit('show-rooms');
  }
}
</script>

<template>
  <button
    :class="[
      'accommodation-product-card-content',
      { 'print-mode': props.printMode },
    ]"
    @mouseenter="emit('mouse-enter-content')"
    @mouseleave="emit('mouse-leave-content')"
    @focusin="emit('mouse-enter-content')"
    @focusout="emit('mouse-leave-content')"
    @keydown.enter="emit('show-details')"
    @click="emit('show-details')"
  >
    <div class="details">
      <div class="upper-section">
        <div class="name">
          {{ props.productCardContent.header }}
        </div>

        <div v-if="props.productCardContent.subheader" class="location">
          {{ props.productCardContent.subheader }}
        </div>

        <div class="info-wrapper">
          <AppStars :amount="props.productCardContent.rating.stars" size="18" />
          <CustomersRatingTag
            v-if="
              isHotelGuestRatingExperiment &&
              props.productCardContent.guestRating
            "
            :guest-rating="props.productCardContent.guestRating"
          />
          <StayTag
            v-if="
              props.productCardContent.cancellationPolicies &&
              props.productCardContent.cancellationPolicies.type ===
                HotelStayTagType.FreeCancellation
            "
            class="cancellation-policy"
            :stay-tag="props.productCardContent.cancellationPolicies"
            :wide="browserStore.isSmallerThan1200px"
            short-version
          />
          <AppTag v-if="props.productCardContent.mealName" type="grey" size="s">
            <template #title>
              <span class="tag-text">
                {{ props.productCardContent.mealName }}
              </span>
            </template>
          </AppTag>
        </div>

        <AccommodationAmenities
          :amenities="props.productCardContent.amenities"
        />
      </div>
      <button
        class="middle-section"
        @click.stop="onEditRoomsBannerClick"
        @mouseenter="emit('mouse-leave-content')"
        @mouseleave="emit('mouse-enter-content')"
        @focusin="emit('mouse-leave-content')"
        @focusout="emit('mouse-enter-content')"
        @keydown.enter="onEditRoomsBannerClick"
      >
        <div
          v-for="element in props.productCardContent.cardDetails"
          :key="element.label + element.description"
          class="row"
        >
          <div :class="['description', { 'single-room': !hasMultipleRooms }]">
            <span v-if="hasMultipleRooms" class="label">
              {{ element.label }}:
            </span>
            {{ element.description }}
          </div>
        </div>
        <AppLinkButton
          v-if="!props.printMode"
          :text="$t('planner.edit_room')"
          size="small"
          @click="emit('show-rooms')"
        />
      </button>
    </div>
  </button>
</template>

<style scoped lang="scss">
.accommodation-product-card-content {
  all: unset;
  height: 100%;
  width: 100%;

  &.print-mode {
    .details {
      .upper-section {
        .name {
          @include on-hover {
            cursor: default;
            text-decoration: none;
          }
        }
      }
    }
  }

  @include for-less-or-equal-than-599-px {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto;
  }

  .details {
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    height: 100%;

    .upper-section {
      padding: var(--spacing-16) var(--spacing-12);

      .name {
        padding: 0;
        margin-bottom: var(--spacing-4);
        @include font-label-xl-bold;
        color: var(--color-primary-20);
        text-decoration: none;

        @include on-hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }

      .location {
        @include font-label-s-light;
        margin-bottom: var(--spacing-4);
        color: var(--color-secondary-60);
      }

      .info-wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacing-8);
        margin-bottom: var(--spacing-16);

        .cancellation-policy {
          width: fit-content;
        }
      }

      .tag-text {
        @include font-label-s-bold;
      }
    }

    .middle-section {
      all: unset;
      padding: var(--spacing-16);
      background: var(--color-secondary-97);
      border: 1px solid var(--color-secondary-93);
      border-radius: var(--border-radius-8);
      margin: auto var(--spacing-12) var(--spacing-16) var(--spacing-12);

      .row {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-8);

        .description {
          @include font-label-s-light;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          width: 0;
          min-width: 100%;

          .label {
            @include font-label-s-bold;
            white-space: nowrap;
          }

          &.single-room {
            white-space: normal;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-height: var(--line-heights-14);
          }
        }
      }
    }
  }

  .price {
    align-self: center;
  }
}
</style>
